import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import {
  getSensorAccountById,
  getSensors,
  searchSensorAccountsPaginated,
} from '../api';
import type { SensorsFilters } from '../types';

// Re-export from inactive-sensors for reuse
export {
  useFleetSummary,
  useSensorDetail,
} from '@/features/operations-inactive-sensors/hooks/useInactiveSensors';

// =============================================================================
// Query Keys Factory
// =============================================================================

export const sensorsKeys = {
  all: ['sensors'] as const,
  list: (filters?: SensorsFilters) =>
    [...sensorsKeys.all, 'list', filters] as const,
  accountsInfinite: (search: string) =>
    [...sensorsKeys.all, 'accounts', 'infinite', search] as const,
  account: (id: string) => [...sensorsKeys.all, 'accounts', 'detail', id] as const,
};

// =============================================================================
// Query Hooks
// =============================================================================

export function useSensors(filters: SensorsFilters = {}) {
  return useQuery({
    queryKey: sensorsKeys.list(filters),
    queryFn: () => getSensors(filters),
    staleTime: 30 * 1000,
  });
}

const ACCOUNT_PAGE_SIZE = 50;

export function useSensorAccountsInfinite(search: string) {
  return useInfiniteQuery({
    queryKey: sensorsKeys.accountsInfinite(search),
    queryFn: ({ pageParam = 0 }) =>
      searchSensorAccountsPaginated({
        search,
        pageNumber: pageParam,
        pageLength: ACCOUNT_PAGE_SIZE,
      }),
    getNextPageParam: (lastPage) => {
      const loaded = (lastPage.pageNumber + 1) * lastPage.pageLength;
      return loaded < lastPage.total ? lastPage.pageNumber + 1 : undefined;
    },
    initialPageParam: 0,
    staleTime: 5 * 60 * 1000,
  });
}

export function useSensorAccount(accountId: string | undefined) {
  return useQuery({
    queryKey: sensorsKeys.account(accountId ?? ''),
    queryFn: () => getSensorAccountById(accountId!),
    enabled: !!accountId && accountId !== 'all',
    staleTime: 5 * 60 * 1000,
  });
}
